import type { CSSProperties, FC } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Calendar, CheckCircle2, ChevronRight } from 'lucide-react';
import type {
  PageOfShipmentDto,
  ShipmentDto,
  ShipmentStatus,
  ShipmentTaskSummaryDto,
} from '@/api';
import {
  Badge,
  Card,
  CardHeader,
  CardTitle,
  Empty,
  Select,
  Skeleton,
  TablePagination,
  cn,
} from '@/ui';

import { today } from '../../lib/dates';
import { formatDate, toNumber } from '../../lib/format';
import { resolveNextTaskText } from '../../lib/nextTask';
import { SHIPMENT_STATUSES } from '../../lib/shipmentStatus';
import {
  PAGE_SIZE,
  TASK_STATUSES,
  type ResolvedTasksSearch,
} from './lib/searchParams';

const SKELETON_ROWS = 4;

const formatTaskCount = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return `${count} задача`;
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return `${count} задачи`;
  return `${count} задач`;
};

export interface TaskListProps {
  resolved: ResolvedTasksSearch;
  tasks: PageOfShipmentDto | undefined;
  isPending: boolean;
  /** Сводка по выбранному клиенту - счётчики чипов. */
  counts: ShipmentTaskSummaryDto | undefined;
  /** Клиенты с задачами - из сводки по всем клиентам. */
  partners: ShipmentTaskSummaryDto['taskPartners'];
  onPartnerChange: (partner: string | undefined) => void;
  onStatusChange: (status: ShipmentStatus | undefined) => void;
  onPageChange: (page: number) => void;
}

/*
 * «Мои задачи на сегодня» - .tasks-card макета, но во всю ширину страницы, как
 * остальные блоки: выбор клиента, чипы статусов со счётчиками, строки задач и
 * пагинация. Задачи - по ближайшей плановой дате; что
 * считается задачей, решает сервер (взятые в работу и план на ближайший месяц).
 */
export const TaskList: FC<TaskListProps> = ({
  resolved,
  tasks,
  isPending,
  counts,
  partners,
  onPartnerChange,
  onStatusChange,
  onPageChange,
}) => {
  const rows = tasks?.items ?? [];
  const total = toNumber(tasks?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(tasks?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  const countOf = (status: ShipmentStatus): number =>
    toNumber(
      counts?.tasksByStatus.find((item) => item.status === status)?.count ?? 0,
    );
  const allCount = TASK_STATUSES.reduce(
    (sum, status) => sum + countOf(status),
    0,
  );

  return (
    <Card className="mb-[22px]">
      <CardHeader className="flex-wrap gap-2">
        <CardTitle>Мои задачи на сегодня</CardTitle>
        <div className="flex items-center gap-2">
          <Select
            aria-label="Клиент"
            className="h-[30px] w-[200px] text-[12.5px]"
            value={resolved.partner ?? ''}
            onChange={(event) =>
              onPartnerChange(event.target.value || undefined)
            }
          >
            <option value="">Все клиенты</option>
            {partners.map((partner) => (
              <option key={partner.partnerId} value={partner.partnerId}>
                {partner.partnerName}
              </option>
            ))}
          </Select>
          <span className="text-xs whitespace-nowrap text-ink-500">
            {formatTaskCount(total)}
          </span>
          <Link
            to="/shipments"
            className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-[550] whitespace-nowrap text-ink-700 hover:bg-ink-100"
          >
            Все отгрузки <ArrowRight aria-hidden className="size-3" />
          </Link>
        </div>
      </CardHeader>

      <div
        role="group"
        aria-label="Статус задачи"
        className="flex flex-wrap gap-2 border-b border-ink-150 px-[18px] py-3.5"
      >
        <StatusChip
          label="Все"
          count={allCount}
          isActive={resolved.status === undefined}
          onClick={() => onStatusChange(undefined)}
        />
        {TASK_STATUSES.map((status) => {
          const isActive = resolved.status === status;

          return (
            <StatusChip
              key={status}
              label={SHIPMENT_STATUSES[status].label}
              color={`var(--color-st-${SHIPMENT_STATUSES[status].tone})`}
              count={countOf(status)}
              isActive={isActive}
              onClick={() => onStatusChange(isActive ? undefined : status)}
            />
          );
        })}
      </div>

      {isPending && (
        <div className="flex flex-col gap-3 px-[18px] py-4">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} className="h-9" />
          ))}
        </div>
      )}

      {!isPending && rows.length === 0 && (
        <Empty icon={<CheckCircle2 aria-hidden className="size-8" />}>
          Все задачи на сегодня выполнены
        </Empty>
      )}

      {rows.map((shipment) => (
        <TaskRow key={shipment.id} shipment={shipment} />
      ))}

      {!isPending && total > 0 && (
        <TablePagination
          className="border-t border-ink-150 bg-ink-50 px-4 py-2.5 text-xs text-ink-500"
          page={resolved.page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          summary={`Показаны ${firstOnPage}-${lastOnPage} из ${total}`}
        />
      )}
    </Card>
  );
};

/** Чип статуса - .task-filter макета: точка цвета статуса, подпись, счётчик. */
const StatusChip: FC<{
  label: string;
  /** Цвет статуса CSS-переменной; без него - зелёный бренда, как у «Все». */
  color?: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}> = ({ label, color, count, isActive, onClick }) => (
  <button
    type="button"
    aria-pressed={isActive}
    disabled={count === 0 && !isActive}
    onClick={onClick}
    style={
      { '--tf-color': color ?? 'var(--color-brand-green)' } as CSSProperties
    }
    className={cn(
      'inline-flex h-[30px] cursor-pointer items-center gap-[7px] rounded-full border border-ink-200 bg-white px-[11px] text-[12.5px] font-[550] text-ink-700 transition-colors hover:enabled:border-ink-300 hover:enabled:bg-ink-50 disabled:cursor-default disabled:opacity-45',
      isActive &&
        'border-(--tf-color) bg-[color-mix(in_srgb,var(--tf-color)_10%,white)] text-ink-900 hover:enabled:border-(--tf-color) hover:enabled:bg-[color-mix(in_srgb,var(--tf-color)_10%,white)]',
    )}
  >
    {color !== undefined && (
      <span aria-hidden className="size-[7px] rounded-full bg-(--tf-color)" />
    )}
    <span>{label}</span>
    <span
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-ink-100 px-[5px] text-[11.5px] font-[650] text-ink-500 tabular-nums',
        isActive &&
          'bg-[color-mix(in_srgb,var(--tf-color)_18%,white)] text-ink-800',
      )}
    >
      {count}
    </span>
  </button>
);

/*
 * Строка задачи - .task-row макета: что сделать (то же правило, что баннер
 * карточки), клиент и номер, статус, плановая дата (прошла - красная). Строка
 * целиком - ссылка на карточку: текст задачи растянут на неё псевдоэлементом.
 */
const TaskRow: FC<{ shipment: ShipmentDto }> = ({ shipment }) => {
  const status = SHIPMENT_STATUSES[shipment.status];
  const isOverdue = shipment.plannedDate < today();

  return (
    <div className="relative grid grid-cols-[minmax(0,1fr)_140px_110px_20px] items-center gap-3.5 border-b border-ink-150 px-[18px] py-3 text-[13px] last:border-b-0 hover:bg-ink-50 has-[a:focus-visible]:bg-ink-50">
      <div className="min-w-0">
        <Link
          to="/shipments/$shipmentId"
          params={{ shipmentId: shipment.id }}
          className="block font-[550] text-ink-900 outline-none after:absolute after:inset-0 after:content-['']"
        >
          {resolveNextTaskText(shipment)?.title ?? status.label}
        </Link>
        <div className="mt-0.5 truncate text-xs text-ink-500">
          {shipment.partnerName} · №{' '}
          <span className="font-mono">{shipment.number}</span>
          {shipment.isDelayed && (
            <>
              {' '}
              ·{' '}
              <span
                className="relative z-10 text-st-delayed"
                title={shipment.delayReason ?? undefined}
              >
                Задержано
              </span>
            </>
          )}
        </div>
      </div>
      <div>
        <Badge tone={status.tone} withDot>
          {status.label}
        </Badge>
      </div>
      <div
        className={cn(
          'flex items-center gap-1.5 text-[12.5px]',
          isOverdue ? 'text-st-delayed' : 'text-ink-600',
        )}
      >
        <Calendar aria-hidden className="size-[13px]" />
        {formatDate(shipment.plannedDate)}
      </div>
      <ChevronRight aria-hidden className="size-4 text-ink-400" />
    </div>
  );
};
